/**
 * A list's filters live in the URL's search params, so a filtered list can be linked, survives a
 * reload and goes back with the browser. A route declares its filters; anything else in the URL
 * is dropped.
 *
 *   const filters = filterSearch({ status: ["open", "closed"], customer_id: "text" });
 *   createRoute({ path: "/orders", validateSearch: filters.validate, ... })
 */
export type FilterSpec = Record<string, "text" | readonly string[]>;

export type FilterValues<S extends FilterSpec> = {
  [K in keyof S]?: S[K] extends readonly (infer V)[] ? V : string;
};

export function filterSearch<S extends FilterSpec>(spec: S) {
  const validate = (search: Record<string, unknown>): FilterValues<S> => {
    const out: Record<string, string> = {};
    for (const [key, allowed] of Object.entries(spec)) {
      const value = search[key];
      if (typeof value !== "string" || value === "") continue;
      if (allowed === "text" || allowed.includes(value)) out[key] = value;
    }
    return out as FilterValues<S>;
  };
  /** The filters that are set, for chips and "No … match these filters". */
  const active = (values: FilterValues<S>) =>
    Object.entries(values).filter(([, v]) => v !== undefined && v !== "") as [keyof S & string, string][];
  return { validate, active };
}
